import React,{useState,useRef,useCallback,useEffect} from 'react';
import {LeaguesPage,PlayerLeaguesView} from '../components/Leagues';
import {Flag,ReorderableBlocks} from '../components/Shared';
import {api} from '../lib/api';
import {A11Y_KEYS,ADDON_PRICING,COUNTRY_CURRENCY,CURRENCIES,FLAGS,PAYMENT_FREQUENCIES,PAYMENT_STATUSES,PRICING,SLOTS} from '../lib/constants';

export function computeAccessStatus(org){
  if(!org)return"trial";
  if(org.subscription_active)return"active";
  if(org.trial_ends_at&&new Date(org.trial_ends_at).getTime()>Date.now())return"trial";
  return"expired";
}

export function trialDaysLeft(org){
  if(!org?.trial_ends_at)return 0;
  const ms=new Date(org.trial_ends_at).getTime()-Date.now();
  return Math.max(0,Math.ceil(ms/(1000*60*60*24)));
}

// Whether a season's end date has come and gone — used to nudge a head coach to
// start a new season (or push the date back) rather than leaving the app silently
// stuck on a season that's over. Treated as ending at the close of that day in the
// coach's own local time, so the reminder doesn't flip on first thing that morning.
export function seasonHasEnded(season){
  if(!season?.end_date)return false;
  const end=new Date(season.end_date+"T23:59:59");
  return !isNaN(end)&&end.getTime()<Date.now();
}

export function computeEntitlements(org){
  const status=computeAccessStatus(org);
  const full=status!=="expired";
  const tier=(org?.plan_tier==="multi")?"multi":"single";
  const billingCycle=(org?.billing_cycle==="yearly")?"yearly":"monthly";
  // During trial everything is unlocked regardless of which tier/add-ons were picked at
  // checkout — a trialing club can freely try multiple teams / player & parent accounts
  // before their first charge goes through. Limits only kick in once actually billed
  // (status "active"); an expired/cancelled trial has none of this at all.
  return {
    status, // "trial" | "active" | "expired"
    daysLeft:trialDaysLeft(org),
    tier, // "single" | "multi" — the org's chosen/subscribed tier (defaults to "single")
    billingCycle, // "monthly" | "yearly"
    maxTeams:status==="expired"?0:status==="trial"?Infinity:(tier==="multi"?Infinity:1),
    maxCoaches:full?Infinity:0,
    groups:full,
    playerAccounts:status==="expired"?false:status==="trial"?true:!!org?.player_addon,
    parentAccounts:status==="expired"?false:status==="trial"?true:!!org?.parent_addon,
  };
}

// Monthly-equivalent price for the tier + whichever add-ons are on, at the given
// billing cycle — used both for the "Subscribe" price breakdown and for showing a
// club's current cost in Settings → Plan.
export function planPriceBreakdown(tier,billingCycle,parentAddon,playerAddon){
  const t=PRICING[tier==="multi"?"multi":"single"];
  const items=[{label:t.label,amount:billingCycle==="yearly"?t.yearly:t.monthly}];
  if(parentAddon)items.push({label:ADDON_PRICING.parent.label,amount:billingCycle==="yearly"?ADDON_PRICING.parent.yearly:ADDON_PRICING.parent.monthly});
  if(playerAddon)items.push({label:ADDON_PRICING.player.label,amount:billingCycle==="yearly"?ADDON_PRICING.player.yearly:ADDON_PRICING.player.monthly});
  const total=items.reduce((s,i)=>s+i.amount,0);
  return {items,total,billingCycle};
}

export function generateFormationSlots(lines){
  const slots=[{x:50,y:90}]; // goalkeeper
  const numLines=lines.length;
  lines.forEach((count,li)=>{
    const y=numLines>1?76-(li*(58/(numLines-1))):45;
    for(let i=0;i<count;i++){
      const x=count===1?50:12+(i*(76/(count-1)));
      slots.push({x,y});
    }
  });
  return slots.map((p,i)=>({id:i,x:p.x,y:p.y,playerId:null}));
}

export function calcAge(d){if(!d)return null;const b=new Date(d);if(isNaN(b))return null;return Math.floor((new Date()-b)/(1000*60*60*24*36.5))/10;}

export function fmtDOB(d){if(!d)return"—";const x=new Date(d);return isNaN(x)?"—":x.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});}

export function today(){return new Date().toISOString().split("T")[0];}

// Good-enough email format check for client-side validation — not RFC-perfect
// (nothing short of actually sending mail is), just enough to catch obvious
// typos/garbage ("x", "bob@", "bob@club") before they hit the server.
export function isValidEmail(s){ return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s||"").trim()); }

// Parses a jersey-number-style input: empty/blank is allowed (returns null —
// "no number set yet"), anything else must be a whole number 0 or greater or
// it's rejected (returns undefined so the caller can tell "invalid" apart
// from "intentionally blank").
export function parseJerseyNumber(raw){
  if(raw===""||raw===null||raw===undefined)return null;
  const s=String(raw).trim();
  if(s==="")return null;
  if(!/^\d+$/.test(s))return undefined; // negative, decimal, or non-numeric
  const n=parseInt(s,10);
  return n>=0?n:undefined;
}
// Normalized (0-1) name similarity, tolerant of typos/spelling differences — used to
// suggest "Is this you?" when a player links their account to a roster entry.

export function levenshtein(a,b){
  const m=a.length,n=b.length;
  const dp=[];
  for(let i=0;i<=m;i++){dp.push([i]);}
  for(let j=1;j<=n;j++){dp[0][j]=j;}
  for(let i=1;i<=m;i++){
    for(let j=1;j<=n;j++){
      dp[i][j]=a[i-1]===b[j-1]?dp[i-1][j-1]:1+Math.min(dp[i-1][j-1],dp[i-1][j],dp[i][j-1]);
    }
  }
  return dp[m][n];
}

export function nameSimilarity(a,b){
  a=(a||"").trim().toLowerCase();b=(b||"").trim().toLowerCase();
  if(!a||!b)return 0;
  if(a===b)return 1;
  const maxLen=Math.max(a.length,b.length);
  return maxLen===0?1:1-levenshtein(a,b)/maxLen;
}
// Ties a modal/overlay's open state to browser history so the back button (Android
// hardware back, browser back, etc.) closes the overlay instead of navigating the
// whole app away. Call from a useEffect: useEffect(()=>modalBackEffect(isOpen,close),[isOpen])

// Shared with any other popstate listener in the app (e.g. the coach/player/parent
// apps' own back-button handling) — when a modal is closed some other way (X button,
// tapping outside, Save), modalBackEffect below calls history.back() itself to consume
// the entry it pushed when opening. That still fires a real popstate event, which would
// otherwise look identical to the user actually pressing back — e.g. re-triggering an
// "open the menu" veto the instant someone closes the menu, making it flash back open.
// modalBackEffect increments this right before its own history.back() call; other
// popstate listeners should check it first and, if set, decrement it and bail out
// without treating the event as a real back press. The setTimeout is just a safety net
// in case that self-triggered pop never actually fires, so this can't get stuck.
export const modalPopSuppress={active:0};

export function modalBackEffect(isOpen, onClose){
  if(!isOpen) return undefined;
  window.history.pushState({__modal:true},"");
  let popped=false;
  function onPop(){ popped=true; onClose(); }
  window.addEventListener("popstate", onPop);
  return ()=>{
    window.removeEventListener("popstate", onPop);
    if(!popped){
      modalPopSuppress.active++;
      window.history.back(); // consume the entry we pushed if closed some other way (X button, save, etc.)
      setTimeout(()=>{ if(modalPopSuppress.active>0)modalPopSuppress.active--; },500);
    }
  };
}

export function fmtDate(d){if(!d)return"—";const x=new Date(d);return isNaN(x)?d:x.toLocaleDateString("en-GB",{weekday:"short",day:"2-digit",month:"short",year:"numeric"});}

// Compact numeric DD/MM/YY — used for payment due dates, where the table needs to stay
// narrow with a lot of period columns side by side rather than the longer "Fri, 17 Jul
// 2026" style fmtDate() gives everywhere else.
export function fmtDateShort(d){
  if(!d)return"—";
  const x=new Date(d);
  if(isNaN(x))return d;
  const dd=String(x.getDate()).padStart(2,"0");
  const mm=String(x.getMonth()+1).padStart(2,"0");
  const yy=String(x.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

export function mkSlots(n){return SLOTS[n].map((p,i)=>({id:i,x:p.x,y:p.y,playerId:null}));}

// ── Match Day time formatting ────────────────────────────────────────────────
// mm:ss for the live in-progress timer (e.g. "23:07").
export function fmtClock(totalSeconds){
  const s=Math.max(0,Math.floor(totalSeconds||0));
  const m=Math.floor(s/60);const ss=s%60;
  return `${String(m).padStart(2,"0")}:${String(ss).padStart(2,"0")}`;
}
// Whole-minutes "63'" style for saved stats tables — matches how playtime is
// normally shown for football (nobody cares about seconds after the fact).
export function fmtMins(totalSeconds){
  return `${Math.round((totalSeconds||0)/60)}'`;
}
// A single on-pitch player's live seconds — their checkpointed total, plus
// whatever's accrued since their current spell started (if the clock is
// actually running right now; spellStartAt is null while paused).
export function livePlayerSeconds(p,nowMs){
  if(!p)return 0;
  if(p.onPitch&&p.spellStartAt)return (p.totalSeconds||0)+Math.max(0,(nowMs-new Date(p.spellStartAt).getTime())/1000);
  return p.totalSeconds||0;
}
// The overall match clock's live seconds — checkpointed total plus time
// since it was last (re)started, if it's currently running.
export function liveClockSeconds(md,nowMs){
  if(!md)return 0;
  if(md.status==="running"&&md.clockRunningSince)return (md.clockElapsedSeconds||0)+Math.max(0,(nowMs-new Date(md.clockRunningSince).getTime())/1000);
  return md.clockElapsedSeconds||0;
}

export function genCode(){return Math.random().toString(36).substring(2,8).toUpperCase();}

// Shared password strength rule for both registering and setting/resetting a
// password: at least 8 characters, containing at least one number and at
// least one special (non-alphanumeric) character. Returns a user-facing
// error string, or null if the password is strong enough.
export function passwordError(pw){
  if(!pw||pw.length<8)return "Password must be at least 8 characters";
  if(!/[0-9]/.test(pw))return "Password must include at least one number";
  if(!/[^A-Za-z0-9]/.test(pw))return "Password must include at least one special character (e.g. !@#$%)";
  return null;
}

export function authErrorMessage(res){
  // Supabase's GoTrue auth server has returned errors in a few different shapes over
  // time ({error:{message}}, {error:"invalid_grant",error_description}, {code,error_code,msg}),
  // and a plain fetch() failure (offline, blocked, etc.) throws before we ever get a
  // response at all. This normalises all of that into a single user-facing string, or
  // null if `res` actually represents success — so the login/register screens never sit
  // on "Loading…" forever just because we didn't recognise the error shape.
  if(!res)return "Something went wrong. Please try again.";
  if(res.error_description)return res.error_description;
  if(res.msg)return res.msg;
  if(res.error){
    if(typeof res.error==="string")return res.error;
    if(res.error.message)return res.error.message;
  }
  if(res.error_code)return String(res.error_code).replace(/_/g," ");
  return null;
}
// ── Accessibility preferences (device-local, like the dark/light mode toggle) ──

export function getA11yPrefs(){
  try{
    return {
      textSize:localStorage.getItem(A11Y_KEYS.textSize)||"normal",
      dyslexiaFont:localStorage.getItem(A11Y_KEYS.dyslexiaFont)==="true",
      colorblindMode:localStorage.getItem(A11Y_KEYS.colorblindMode)||"none",
    };
  }catch(e){ return {textSize:"normal",dyslexiaFont:false,colorblindMode:"none"}; }
}

export function applyA11yPrefs(prefs){
  const html=document.documentElement;
  html.setAttribute("data-text-size",prefs.textSize);
  html.setAttribute("data-dyslexia-font",prefs.dyslexiaFont?"true":"false");
  html.setAttribute("data-colorblind-mode",prefs.colorblindMode);
}

export function setA11yPref(key,value){
  try{localStorage.setItem(A11Y_KEYS[key],String(value));}catch(e){}
  applyA11yPrefs(getA11yPrefs());
}
// Apply immediately on script load, before the first render, so there's no flash of
// unstyled/wrong-size content — same reasoning as the dark/light mode attribute.
if(typeof document!=="undefined")applyA11yPrefs(getA11yPrefs());

export function csvEscape(v){ v=v===null||v===undefined?"":String(v); return /[",\n]/.test(v)?'"'+v.replace(/"/g,'""')+'"':v; }

export function downloadCSV(filename,rows){
  const csv=rows.map(r=>r.map(csvEscape).join(",")).join("\r\n");
  const blob=new Blob(["﻿"+csv],{type:"text/csv;charset=utf-8;"});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a");
  a.href=url;a.download=filename;document.body.appendChild(a);a.click();document.body.removeChild(a);
  setTimeout(()=>URL.revokeObjectURL(url),1000);
}

export function homeAwaySplit(matches){
  const mk=()=>({played:0,won:0,drawn:0,lost:0,gf:0,ga:0});
  const out={home:mk(),away:mk()};
  (matches||[]).forEach(f=>{
    const vt=f.venue_type;
    if(vt!=="home"&&vt!=="away")return;
    const us=f.ourScore??f.our_score??0, them=f.oppScore??f.opp_score??0;
    const b=out[vt];
    b.played++;b.gf+=us;b.ga+=them;
    if(us>them)b.won++; else if(us<them)b.lost++; else b.drawn++;
  });
  return out;
}

export function enrich(f,evts){const fe=(evts||[]).filter(e=>e.fixture_id===f.id);const sc=[],as=[];fe.forEach(e=>{if(e.type==="goal")sc.push({playerId:e.player_id,count:e.count});else as.push({playerId:e.player_id,count:e.count});});return{...f,scorers:sc,assisters:as,slots:f.slots||mkSlots(f.side_count||11),sideCount:f.side_count||11,subs:f.subs||[],ourScore:f.our_score||0,oppScore:f.opp_score||0};}

export function rb(m){if(!m.played)return{label:"Upcoming",color:"var(--c-muted)",bg:"var(--c-surface)"};if(m.ourScore>m.oppScore)return{label:"W",color:"#10b981",bg:"#d1fae5"};if(m.ourScore<m.oppScore)return{label:"L",color:"#ef4444",bg:"#fee2e2"};return{label:"D",color:"#f59e0b",bg:"#fef3c7"};}

// ── Age eligibility logic ────────────────────────────────────────────────────
// Computes a player's age "as of" the cutoff date for a given season + cutoff type.
// Computes a player's age "as of" an explicit cutoff date the coach picks.

export function ageAtCutoff(dob, cutoffDate){
  if(!dob || !cutoffDate) return null;
  const birth=new Date(dob);
  const cutoff=new Date(cutoffDate);
  if(isNaN(birth) || isNaN(cutoff)) return null;
  let age = cutoff.getFullYear() - birth.getFullYear();
  const m = cutoff.getMonth() - birth.getMonth();
  if(m < 0 || (m===0 && cutoff.getDate() < birth.getDate())) age--;
  return age;
}
// Returns true if a player is eligible for an age category as of the cutoff date.
// "Under 15" (U15) means the player's age is at most 14 on the cutoff date — but a
// 14-year-old is also eligible to play up into older groups (U16, U17, U18, U19...),
// just never down into a younger one (U14 and below). So eligibility is age <= cap.

export function isEligible(dob, ageCategory, cutoffDate){
  // Open-ended categories — "Seniors" — have no upper limit, so everyone qualifies
  // regardless of age, including players with no date of birth recorded. Without this
  // the parseInt below would give NaN and the comparison would quietly exclude the
  // entire squad, which looks like an empty player list rather than a bug.
  const cap = /^U\d+$/i.test(String(ageCategory||"")) ? parseInt(String(ageCategory).slice(1),10) : null;
  if(cap===null) return true;
  const age = ageAtCutoff(dob, cutoffDate);
  if(age===null) return false;
  return age <= cap - 1;
}

export function flagCodeFromEmoji(emoji){
  if(!emoji||typeof emoji!=="string") return null;
  const cps=[...emoji].map(c=>c.codePointAt(0));
  if(cps[0]===0x1F3F4){ // black flag + tag sequence (subdivision flags, e.g. England/Scotland/Wales)
    let code="";
    for(let i=1;i<cps.length;i++){
      const cp=cps[i];
      if(cp===0xE007F) break;
      if(cp>=0xE0061&&cp<=0xE007A) code+=String.fromCharCode(cp-0xE0000);
    }
    return code.length>=4 ? code.slice(0,2)+"-"+code.slice(2) : null;
  }
  if(cps.length===2&&cps.every(cp=>cp>=0x1F1E6&&cp<=0x1F1FF)){ // standard 2-letter country flag
    return cps.map(cp=>String.fromCharCode(cp-0x1F1E6+65)).join("").toLowerCase();
  }
  return null;
}

export async function loadBlockOrder(token,userId,pageKey,defaultOrder){
  const rows=await api.get(token,"user_layout_preferences",`user_id=eq.${userId}&page_key=eq.${pageKey}`);
  const saved=rows&&rows[0]&&Array.isArray(rows[0].block_order)?rows[0].block_order:null;
  if(!saved)return defaultOrder;
  // Keep the saved order for blocks that still exist, then append any new
  // blocks (e.g. from an app update) that weren't in the saved order yet.
  return [...saved.filter(id=>defaultOrder.includes(id)),...defaultOrder.filter(id=>!saved.includes(id))];
}

export async function saveBlockOrder(token,userId,pageKey,order){
  const existing=await api.get(token,"user_layout_preferences",`user_id=eq.${userId}&page_key=eq.${pageKey}`);
  if(existing&&existing[0]){
    await api.patchWhere(token,"user_layout_preferences",`user_id=eq.${userId}&page_key=eq.${pageKey}`,{block_order:order,updated_at:new Date().toISOString()});
  } else {
    await api.post(token,"user_layout_preferences",[{user_id:userId,page_key:pageKey,block_order:order}]);
  }
}

// ── ReorderableBlocks ────────────────────────────────────────────────────────
// Drag-and-drop reordering built on Pointer Events (not the HTML5 drag-and-drop
// API, which barely works on touch devices) — so this works the same way with
// a mouse or a finger. Drag by the ⠿ handle to the left of each block; order
// is saved automatically once you let go.

export function resolveLeagueFixtureScore(lf,fixturesById){
  if(lf.owner_final_home_score!=null&&lf.owner_final_away_score!=null){
    return {home:lf.owner_final_home_score,away:lf.owner_final_away_score,status:"confirmed"};
  }
  const homeFx=lf.home_fixture_id?fixturesById[lf.home_fixture_id]:null;
  const awayFx=lf.away_fixture_id?fixturesById[lf.away_fixture_id]:null;
  const homePlayed=homeFx&&homeFx.played;
  const awayPlayed=awayFx&&awayFx.played;
  if(homeFx&&awayFx){
    if(homePlayed&&awayPlayed){
      if(homeFx.our_score===awayFx.opp_score&&homeFx.opp_score===awayFx.our_score){
        return {home:homeFx.our_score,away:homeFx.opp_score,status:"confirmed"};
      }
      return {home:null,away:null,status:"disputed",homeReport:{home:homeFx.our_score,away:homeFx.opp_score},awayReport:{home:awayFx.opp_score,away:awayFx.our_score}};
    }
    return {home:null,away:null,status:"scheduled"};
  } else if(homeFx&&!awayFx){
    if(homePlayed)return {home:homeFx.our_score,away:homeFx.opp_score,status:"confirmed"};
    return {home:null,away:null,status:"scheduled"};
  } else if(!homeFx&&awayFx){
    if(awayPlayed)return {home:awayFx.opp_score,away:awayFx.our_score,status:"confirmed"};
    return {home:null,away:null,status:"scheduled"};
  } else {
    if(lf.manual_home_score!=null&&lf.manual_away_score!=null)return {home:lf.manual_home_score,away:lf.manual_away_score,status:"confirmed"};
    return {home:null,away:null,status:"scheduled"};
  }
}

export function ordinal(n){
  const s=["th","st","nd","rd"],v=n%100;
  return n+(s[(v-20)%10]||s[v]||s[0]);
}

export function leagueTeamName(lt,teamsById){
  return lt.display_name||teamsById[lt.team_id]?.name||lt.external_name||"Unnamed Team";
}

export function computeLeagueTable(league,leagueTeamsList,leagueFixturesList,fixturesById,teamsById){
  const stats={};
  leagueTeamsList.forEach(lt=>{stats[lt.id]={id:lt.id,name:leagueTeamName(lt,teamsById),played:0,won:0,drawn:0,lost:0,gf:0,ga:0,gd:0,pts:0,h2h:{}};});
  leagueFixturesList.forEach(lf=>{
    const resolved=resolveLeagueFixtureScore(lf,fixturesById);
    if(resolved.status!=="confirmed")return;
    const home=stats[lf.home_league_team_id],away=stats[lf.away_league_team_id];
    if(!home||!away)return;
    home.played++;away.played++;
    home.gf+=resolved.home;home.ga+=resolved.away;
    away.gf+=resolved.away;away.ga+=resolved.home;
    if(resolved.home>resolved.away){home.won++;away.lost++;home.pts+=league.points_win;away.pts+=league.points_loss;}
    else if(resolved.home<resolved.away){away.won++;home.lost++;away.pts+=league.points_win;home.pts+=league.points_loss;}
    else {home.drawn++;away.drawn++;home.pts+=league.points_draw;away.pts+=league.points_draw;}
    home.h2h[away.id]=(home.h2h[away.id]||0)+(resolved.home>resolved.away?3:resolved.home===resolved.away?1:0);
    away.h2h[home.id]=(away.h2h[home.id]||0)+(resolved.away>resolved.home?3:resolved.home===resolved.away?1:0);
  });
  const arr=Object.values(stats);
  arr.forEach(s=>{s.gd=s.gf-s.ga;});
  const order=league.tiebreak_order||["GD","GF","GA","H2H"];
  arr.sort((a,b)=>{
    if(b.pts!==a.pts)return b.pts-a.pts;
    for(const tb of order){
      if(tb==="GD"&&b.gd!==a.gd)return b.gd-a.gd;
      if(tb==="GF"&&b.gf!==a.gf)return b.gf-a.gf;
      if(tb==="GA"&&a.ga!==b.ga)return a.ga-b.ga;
      if(tb==="H2H"){const ab=a.h2h[b.id]||0,ba=b.h2h[a.id]||0;if(ba!==ab)return ba-ab;}
    }
    return a.name.localeCompare(b.name);
  });
  return arr;
}

// ── LeaguesPage ───────────────────────────────────────────────────────────────

export function isFeatureOn(org,t){ return t.defaultOn?org?.[t.field]!==false:!!org?.[t.field]; }

export function currencyForCountry(country){ return COUNTRY_CURRENCY[country]||"GBP"; }

export function currencySymbol(code){ return CURRENCIES.find(c=>c.code===code)?.symbol||code||"£"; }

export function freqLabel(f){ return PAYMENT_FREQUENCIES.find(x=>x.key===f)?.label||f; }

export function statusInfo(s){ return PAYMENT_STATUSES.find(x=>x.key===s)||PAYMENT_STATUSES[0]; }

export function toISODate(d){ return d.toISOString().slice(0,10); }

export function perInstallmentAmount(plan,periodsCount){
  if(!plan)return 0;
  if(plan.amount_type==="total"&&periodsCount>0)return (plan.amount||0)/periodsCount;
  return plan.amount||0;
}

export function downloadPlayerCSVTemplate(){
  const headers=["Name","Number","Position","Gender","Date of Birth","Nationality Flag"];
  const example=["Jordan Smith","9","ST","male","2012-04-15","🏴󠁧󠁢󠁥󠁮󠁧󠁿"];
  const csv=[headers.join(","),example.join(",")].join("\r\n");
  const blob=new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8;"});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a");
  a.href=url;
  a.download="player-import-template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function sanitiseNationality(raw){
  const val=(raw||"").trim();
  if(!val) return "🏴󠁧󠁢󠁥󠁮󠁧󠁿"; // default to England flag
  // If it contains the Unicode replacement character or control chars, it's corrupted
  // (e.g. an emoji flag that didn't survive the file's text encoding) — fall back to plain text.
  if(/\uFFFD/.test(val)) return val.replace(/\uFFFD+/g,"").trim() || "🏴󠁧󠁢󠁥󠁮󠁧󠁿";
  // Try to match against our known flag list by country name (case-insensitive)
  const match=FLAGS.find(f=>f.name.toLowerCase()===val.toLowerCase());
  if(match) return match.flag;
  return val; // leave as-is (could already be a flag emoji, or free text we display verbatim)
}

export function parsePlayerCSV(text){
  // Strip BOM if present
  text=text.replace(/^\uFEFF/,"");
  const lines=text.split(/\r\n|\n|\r/).filter(l=>l.trim().length>0);
  if(lines.length===0)return{rows:[],errors:["File is empty"]};

  // Auto-detect delimiter by checking which one splits the header into the most columns
  const candidates=[",",";","\t","|"];
  let delimiter=",";
  let bestCount=1;
  for(const d of candidates){
    const count=lines[0].split(d).length;
    if(count>bestCount){bestCount=count;delimiter=d;}
  }

  function parseLine(line, delim){
    const out=[];let cur="";let inQuotes=false;
    for(let i=0;i<line.length;i++){
      const ch=line[i];
      if(inQuotes){
        if(ch==='"'){ if(line[i+1]==='"'){cur+='"';i++;} else inQuotes=false; }
        else cur+=ch;
      } else {
        if(ch==='"')inQuotes=true;
        else if(ch===delim){out.push(cur);cur="";}
        else cur+=ch;
      }
    }
    out.push(cur);
    return out.map(s=>s.trim());
  }

  let header=parseLine(lines[0], delimiter).map(h=>h.toLowerCase());

  // If even the best delimiter only found 1 column, the header has no real columns --
  // fall back to splitting on runs of whitespace (space/tab) as a last resort.
  if(header.length<=1){
    delimiter=null; // signals whitespace mode
    header=lines[0].trim().split(/\s+/).map(h=>h.toLowerCase());
  }

  function parseRow(line){
    if(delimiter===null) return line.trim().split(/\s+/);
    return parseLine(line, delimiter);
  }

  const idx={
    name: header.findIndex(h=>h.includes("name")),
    number: header.findIndex(h=>h.includes("number")||h==="#"),
    position: header.findIndex(h=>h.includes("position")||h==="pos"),
    gender: header.findIndex(h=>h.includes("gender")||h==="sex"),
    dob: header.findIndex(h=>h.includes("birth")||h.includes("dob")),
    nationality: header.findIndex(h=>h.includes("nationality")||h.includes("flag")||h.includes("country")),
  };
  const errors=[];
  if(idx.name===-1){
    errors.push('Couldn\'t find a "Name" column -- check your file has a header row matching the template');
    return{rows:[],errors};
  }

  function normaliseDate(raw){
    if(!raw) return "";
    let dob=raw.trim();
    if(/^\d{4}-\d{2}-\d{2}$/.test(dob)) return dob; // already ISO
    // dd/mm/yyyy or dd-mm-yyyy or d/m/yy
    let m=dob.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
    if(m){
      let [,d,mo,y]=m;
      if(y.length===2) y = (parseInt(y)>30?"19":"20")+y;
      return `${y}-${mo.padStart(2,"0")}-${d.padStart(2,"0")}`;
    }
    // yyyy/mm/dd
    m=dob.match(/^(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})$/);
    if(m){
      const [,y,mo,d]=m;
      return `${y}-${mo.padStart(2,"0")}-${d.padStart(2,"0")}`;
    }
    // Try native Date parsing as last resort (handles "15 April 2012", "Apr 15 2012" etc)
    const parsed=new Date(dob);
    if(!isNaN(parsed) && parsed.getFullYear()>1900 && parsed.getFullYear()<2100){
      const y=parsed.getFullYear();
      const mo=String(parsed.getMonth()+1).padStart(2,"0");
      const d=String(parsed.getDate()).padStart(2,"0");
      return `${y}-${mo}-${d}`;
    }
    return null; // unrecognised
  }

  const rows=[];
  for(let i=1;i<lines.length;i++){
    const cells=parseRow(lines[i]);
    if(cells.every(c=>c==="")) continue;
    const name=idx.name>-1?cells[idx.name]:"";
    if(!name){ errors.push(`Row ${i+1}: missing name, skipped`); continue; }
    const rawGender=(idx.gender>-1?(cells[idx.gender]||""):"").toLowerCase();
    let gender = rawGender.startsWith("f") ? "female" : "male";
    if(!rawGender || !(rawGender.startsWith("f")||rawGender.startsWith("m"))){
      errors.push(`Row ${i+1} (${name}): gender missing or unrecognised, defaulted to "male" -- please check`);
    }
    const rawPosition=idx.position>-1?cells[idx.position]:"";
    if(!rawPosition){
      errors.push(`Row ${i+1} (${name}): position missing, defaulted to "CM" -- please check`);
    }
    let dob="";
    if(idx.dob>-1 && cells[idx.dob]){
      const normalised=normaliseDate(cells[idx.dob]);
      if(normalised===null){ errors.push(`Row ${i+1}: unrecognised date "${cells[idx.dob]}", left blank`); }
      else dob=normalised;
    }
    rows.push({
      name,
      number: idx.number>-1?cells[idx.number]:"",
      position: (rawPosition||"CM").toUpperCase(),
      gender,
      dob,
      nationality: sanitiseNationality(idx.nationality>-1?cells[idx.nationality]:""),
    });
  }
  return{rows,errors};
}

export function exportPlayersCSV(players, teamName){
  const headers=["Number","Name","Position","Secondary Position","Nationality","Date of Birth","Age","Status","Goals","Assists"];
  function esc(val){
    const s=String(val??"");
    if(s.includes(",")||s.includes('"')||s.includes("\n")){
      return '"'+s.replace(/"/g,'""')+'"';
    }
    return s;
  }
  const rows=players
    .slice()
    .sort((a,b)=>(a.number||999)-(b.number||999))
    .map(p=>{
      const age=calcAge(p.dob);
      return [
        p.number||"",
        p.name,
        p.position,
        p.secondary_position||"",
        p.nationality,
        p.dob?fmtDOB(p.dob):"",
        age!==null?age.toFixed(1):"",
        p.status,
        p.goals||0,
        p.assists||0,
      ].map(esc).join(",");
    });
  const csv=[headers.join(","),...rows].join("\r\n");
  const blob=new Blob(["\uFEFF"+csv],{type:"text/csv;charset=utf-8;"});
  const url=URL.createObjectURL(blob);
  const a=document.createElement("a");
  const safeTeam=(teamName||"team").replace(/[^a-z0-9]+/gi,"-").toLowerCase();
  const dateStr=new Date().toISOString().split("T")[0];
  a.href=url;
  a.download=`${safeTeam}-players-${dateStr}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── ParentApp ─────────────────────────────────────────────────────────────────
// ── PlayerLeaguesView (read-only, used by both PlayerApp and ParentApp) ──────────

export function fmtTenure(dateStr){
  if(!dateStr)return"—";
  const start=new Date(dateStr);
  if(isNaN(start))return"—";
  const now=new Date();
  let months=(now.getFullYear()-start.getFullYear())*12+(now.getMonth()-start.getMonth());
  if(now.getDate()<start.getDate())months--;
  if(months<1){
    const days=Math.max(0,Math.floor((now-start)/(1000*60*60*24)));
    return days<=1?"today":`${days} day${days===1?"":"s"}`;
  }
  if(months<24)return `${months} month${months===1?"":"s"}`;
  const years=Math.floor(months/12),rem=months%12;
  return `${years} year${years===1?"":"s"}${rem>0?` ${rem} mo`:""}`;
}

export function arrowHeadPtsStatic(x1,y1,x2,y2,size){
  const angle=Math.atan2(y2-y1,x2-x1);
  const a1=angle+Math.PI*0.85,a2=angle-Math.PI*0.85;
  return `${x2},${y2} ${x2+size*Math.cos(a1)},${y2+size*Math.sin(a1)} ${x2+size*Math.cos(a2)},${y2+size*Math.sin(a2)}`;
}

export function renderStaticShape(d,key){
  if(d.type==="circle")return <circle key={key} cx={d.cx} cy={d.cy} r={d.r} fill="none" stroke={d.color} strokeWidth="0.8"/>;
  if(d.type==="text")return <text key={key} x={d.x} y={d.y} fontSize={d.size||5} textAnchor="middle" dominantBaseline="central" fontWeight="700" fill={d.color} stroke="#000" strokeWidth="0.4" paintOrder="stroke">{d.text}</text>;
  if(d.type==="line")return <line key={key} x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} stroke={d.color} strokeWidth="0.8" strokeDasharray={d.style==="dashed"?"3 2.5":undefined}/>;
  if(d.type==="oppplayer")return(
    <g key={key} transform={`translate(${d.x},${d.y})`} opacity="0.55">
      <circle cx="0" cy="-3.4" r="1.7" fill="#9ca3af" stroke="#374151" strokeWidth="0.3"/>
      <path d="M -3 3.2 Q -3 -1.6 0 -1.6 Q 3 -1.6 3 3.2 Z" fill="#9ca3af" stroke="#374151" strokeWidth="0.3"/>
    </g>
  );
  if(d.type==="ghostplayer")return(
    <g key={key} transform={`translate(${d.x},${d.y})`} opacity="0.4">
      <circle cx="0" cy="0" r="3.2" fill={d.color||"#ffffff"} stroke="#fff" strokeWidth="0.3" strokeDasharray="1 0.8"/>
    </g>
  );
  return(<g key={key}>
    <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} stroke={d.color} strokeWidth="0.8" strokeDasharray={d.style==="dashed"?"3 2.5":undefined}/>
    <polygon points={arrowHeadPtsStatic(d.x1,d.y1,d.x2,d.y2,3)} fill={d.color}/>
  </g>);
}

